import React from 'react'
import { Group } from '@visx/group'
import { animated, useSpring } from '@react-spring/web'
import { Arc, arc } from '@visx/shape'
import { scaleLinear } from '@visx/scale'

const defaultMargin = { top: 0, right: 0, bottom: 0, left: 0 }

export type GaugeChartProps = {
  width: number
  height: number
  margin?: typeof defaultMargin
  value: number
  color: string
  label?: string
  backgroundColor?: string
  gaugeThickness?: number
  backgroundGaugeColor?: string
  min?: number
  max?: number
  showValue?: boolean
  valueFormatter?: (value: number) => string
  showLegend?: boolean
  indicatorLine?: number | null
  indicatorLineColor?: string
  unit?: string
  animate?: boolean
}

export function GaugeChart({
  width = 400,
  height = 250,
  margin = defaultMargin,
  value = 50,
  color = '#11B424',
  label = '',
  backgroundColor = '#FFF',
  gaugeThickness = 20,
  backgroundGaugeColor = '#F0F4F5',
  min = 0,
  max = 100,
  showValue = true,
  valueFormatter = (value) =>
    new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value),
  indicatorLine = null,
  indicatorLineColor = '#69717A',
  unit = '',
  animate = true,
}: GaugeChartProps) {
  if (width < 10) return null

  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom
  const radius = Math.min(innerWidth, innerHeight) / 2
  const centerY = innerHeight * 0.6
  const centerX = innerWidth / 2

  // Calculate the angle based on the value - flip the range
  const valueScale = scaleLinear({
    domain: [min, max],
    range: [Math.PI * -0.5, Math.PI * 0.5], // From 90 degrees to 270 degrees
  })

  const targetAngle = valueScale(Math.min(Math.max(value, min), max))

  // Animation for the gauge fill - remove animatedValue
  const { animatedAngle } = useSpring({
    from: animate ? { animatedAngle: Math.PI * -0.5 } : { animatedAngle: targetAngle },
    to: { animatedAngle: targetAngle },
    config: { tension: 120, friction: 14 },
  })

  // Calculate indicator line angle if provided
  const indicatorAngle =
    indicatorLine !== null ? valueScale(Math.min(Math.max((indicatorLine / 100) * (max - min) + min, min), max)) : null

  // Animation for the background
  const backgroundAnimation = useSpring({
    from: animate ? { opacity: 0, scale: 0.9 } : { opacity: 1, scale: 1 },
    to: { opacity: 1, scale: 1 },
    config: { tension: 300, friction: 20 },
  })

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width={innerWidth} height={innerHeight - 20}>
          <rect rx={14} width={innerWidth} height={innerHeight - 20} fill={backgroundColor} />
          <Group top={centerY + margin.top} left={centerX + margin.left}>
            {/* Background gauge with animation */}
            <animated.g
              style={{
                opacity: backgroundAnimation.opacity,
                transform: backgroundAnimation.scale.to((s) => `scale(${s})`),
              }}
            >
              <Arc
                outerRadius={radius}
                innerRadius={radius - gaugeThickness}
                startAngle={Math.PI * 0.5} // 270 degrees (top)
                endAngle={Math.PI * -0.5} // 90 degrees (bottom)
                fill={backgroundGaugeColor}
              />
            </animated.g>

            {/* Small white gap at end of gauge */}
            <animated.path
              d={animatedAngle.to(
                (angle) =>
                  arc({
                    outerRadius: radius,
                    innerRadius: radius - gaugeThickness,
                    startAngle: angle, // Current animated angle
                    endAngle: angle + 0.05,
                    cornerRadius: 0,
                  })(value) || ''
              )}
              fill="white"
            />

            {/* Value gauge with animation */}
            <animated.path
              d={animatedAngle.to(
                (angle) =>
                  arc({
                    outerRadius: radius,
                    innerRadius: radius - gaugeThickness,
                    startAngle: Math.PI * -0.5, // 90 degrees (top)
                    endAngle: angle,
                    cornerRadius: 0,
                  })(value) || ''
              )}
              fill={color}
              stroke="#FFF"
              strokeWidth="0"
            />

            {/* Gray indicator line - only render if indicatorLine is provided */}
            {indicatorAngle !== null && (
              <animated.rect
                x={-0.5}
                y={-radius - 2}
                width="1"
                height={gaugeThickness + 4}
                fill={indicatorLineColor}
                transform={`rotate(${(indicatorAngle + 0.105) * (180 / Math.PI)})`}
                style={{
                  opacity: backgroundAnimation.opacity,
                }}
              />
            )}

            {/* Display value in the center with unit - without animation */}
            {showValue && (
              <animated.text
                x={0}
                y={0}
                className="text-primary"
                textAnchor="middle"
                fontSize={24}
                fontWeight="400"
                fontFamily="TT Hoves Pro, ui-sans-serif, system-ui, sans-serif"
                style={{
                  opacity: backgroundAnimation.opacity,
                }}
              >
                {`${valueFormatter(value)}${unit}`}
              </animated.text>
            )}

            {/* Add the three labels under the gauge - moved closer to the gauge */}
            <animated.text
              x={-radius + gaugeThickness / 2 - 15}
              y={radius / 2 - 15}
              textAnchor="start"
              fontSize={12}
              fontWeight="400"
              fontFamily="TT Hoves Pro, ui-sans-serif, system-ui, sans-serif"
              className="text-secondary"
              style={{ opacity: backgroundAnimation.opacity }}
            >
              Low
            </animated.text>
            <animated.text
              x={0}
              y={radius / 2 - 15}
              textAnchor="middle"
              fontSize={12}
              className="text-secondary"
              style={{ opacity: backgroundAnimation.opacity }}
            >
              KPI
            </animated.text>
            <animated.text
              x={radius - gaugeThickness / 2 + 15}
              y={radius / 2 - 15}
              textAnchor="end"
              fontSize={12}
              className="text-secondary"
              style={{ opacity: backgroundAnimation.opacity }}
            >
              High
            </animated.text>
          </Group>
        </svg>
      </div>

      {label && (
        <animated.div className="relative text-primary text-sm" style={{ opacity: backgroundAnimation.opacity }}>
          {label}
        </animated.div>
      )}
    </div>
  )
}
